import React, { useState, useEffect, useRef } from 'react';
import './ManningInfo.css'; // Reuse ManningInfo styles for Opportunity
import logo from './assets/dg-logo.png';
import userIcon from './assets/user-icon.png';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Navigation from './Navigation';
import OpportunitySlidingDrawer from './OpportunitySlidingDrawer';
import { useParams } from 'react-router-dom';
import { fetchWithAuth } from './utils';
import AddOpportunityModal from './AddOpportunityModal';
import OpportunitySheetModal from './OpportunitySheetModal';

interface Region {
  company_code: string;
  region_code: string;
  region_name: string;
}

interface Opportunity {
  project_name: string;
  status: string;
  start_date: string;
  due_date: string;
  priority: string;
  created_by: string;
  created_date: string;
  opportunity_id: string;
  region_code: string;
  progress_status: string;
}

// Utility functions for caching
const getCachedData = (key: string) => {
  const cached = localStorage.getItem(key);
  if (cached) {
    return JSON.parse(cached);
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  localStorage.setItem(key, JSON.stringify(data));
};

const Opportunity: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [opportunityData, setOpportunityData] = useState<Opportunity[]>([]);
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const [quickFilterText, setQuickFilterText] = useState<string>(''); 
  const [gridApi, setGridApi] = useState<any>(null); 
  const [selectedSubLink, setSelectedSubLink] = useState<'manning' | 'opportunity' | 'project' | 'openpros' | 'recruiting'>('manning');
  const [selectedRowData, setSelectedRowData] = useState<Opportunity | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const drawerRef = useRef<HTMLDivElement>(null); 
  const { regionCode } = useParams<{ regionCode: string }>(); 
  const [statusFilter, setStatusFilter] = useState('Forecasted');
  const [displaySheetView, setDisplaySheetView] = useState(true);
  const [selectedCardData, setSelectedCardData] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('All');
  const [sheetSearch, setSheetSearch] = useState('');


  type PriorityKey = 'Urgent' | 'Critical' | 'High' | 'Medium' | 'Low';
  // Update your mapping:
  const priorityIcons: { [key: string]: { icon: React.ReactNode, color: string, label: string } } = {
    'Urgent':    { icon: '⏰', color: '#E53E3E', label: 'Urgent' },    // Red clock
    'Critical':  { icon: '⚡', color: '#D69E2E', label: 'Critical' },  // Lightning
    'High':      { icon: '⬆️', color: '#ED8936', label: 'High' },     // Orange up arrow
    'Medium':    { icon: '⬅️', color: '#ECC94B', label: 'Medium' },   // Yellow left arrow
    'Low':       { icon: '⬇️', color: '#38A169', label: 'Low' },      // Green down arrow
  };

  const priorityOptions = [
  'All',
  'Urgent',
  'High',
  'Medium',
  'Low',
  ];

  const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Completed'] as const;
  type StatusType = typeof STATUS_OPTIONS[number];
  const [openStatusMenuId, setOpenStatusMenuId] = useState<string | null>(null);


  const statusIcons: Record<StatusType, { icon: string; color: string; label: string }> = {
    'Not Started': { icon: '⚪', color: '#bdbdbd', label: 'Not Started' },
    'In Progress': { icon: '⏳', color: '#ff9800', label: 'In Progress' },
    'Completed':   { icon: '✅', color: '#28a745', label: 'Completed' },
  };
  

  // Fetch regions on mount, check local storage first
  useEffect(() => {
    const fetchRegions = async () => {
      const cachedRegions = getCachedData('regions_new');
      if (cachedRegions) {
        setRegions(cachedRegions);
        return;
      }

      try {
        const url = 'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_regions&company_code=DG'
        const data = await fetchWithAuth(url);
        setRegions(data);
        setCachedData('regions_new', data); // Cache the regions
      } catch (error) {
        console.error('Error fetching regions:', error);
      }
    };

    fetchRegions();
  }, []);

  // Fetch opportunity data when regionCode changes, check local storage first
  useEffect(() => {
    if (regionCode) {
        fetchOpportunities(regionCode);
    }
  }, [regionCode]);

  // Fetch opportunity data
const fetchOpportunities = async (region_code: string) => {
  setLoading(true); // <-- start loading
  try {
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_all_opportunity&company_code=DG&region_code=${region_code}`
    const data = await fetchWithAuth(url);
    const dataWithRegion = data.map((op: any) => ({
      ...op,
      region_code: op.region_code || region_code,
    }));
    setOpportunityData(dataWithRegion);
    setCachedData(`opportunities_${region_code}`, dataWithRegion);
  } catch (error) {
    console.error('Error fetching opportunity data:', error);
  }
  setLoading(false); // <-- end loading
};

  // Handle expanding/collapsing regions in the sidebar
  const toggleRegion = (regionCode: string) => {
    if (expandedRegion === regionCode) {
      setExpandedRegion(null);
    } else {
      setExpandedRegion(regionCode);
      setSelectedSubLink('opportunity');

      const cachedOpportunities = getCachedData(`opportunities_${regionCode}`);
      if (!cachedOpportunities) {
        fetchOpportunities(regionCode); // Fetch if not cached
      }
    }
  };

  // Handle row click in grid
  const onRowClicked = (data: Opportunity) => {
    setSelectedRowData(data);
    setIsDrawerOpen(true); // Open the drawer when a row is clicked
  };

  // Grid columns for Opportunity data
  const columnDefs: ColDef[] = [
    { headerName: 'Project Name', field: 'project_name', sortable: true, filter: true },
    { headerName: 'Status', field: 'status', sortable: true, filter: true },
    { headerName: 'Start Date', field: 'start_date', sortable: true, filter: true },
    { headerName: 'Due Date', field: 'due_date', sortable: true, filter: true },
    { headerName: 'Priority', field: 'priority', sortable: true, filter: true },
    { headerName: 'Created By', field: 'created_by', sortable: true, filter: true },
    { headerName: 'Created Date', field: 'created_date', sortable: true, filter: true },
  ];

  const onGridReady = (params: any) => {
    setGridApi(params.api); 
  };

  // Handle search/filter functionality for grid
  const onQuickFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filterValue = event.target.value;
    setQuickFilterText(filterValue);

    if (gridApi && typeof gridApi.setQuickFilter === 'function') {
      gridApi.setQuickFilter(filterValue);
    }
  };

  // Close drawer when clicking outside, but not inside the drawer itself
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        setIsDrawerOpen(false); // Close the drawer if clicking outside
      }
    };

    if (isDrawerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDrawerOpen]);

  const [username, setUsername] = useState('');
  useEffect(() => {
    const sessionData = localStorage.getItem('userToken');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        setUsername(parsed.username || '');
      } catch (error) {
        console.error('Failed to parse session userToken:', error);
      }
    }
  }, []);

  const [showLogout, setShowLogout] = useState(false);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
    setShowLogout(true);
  };

  const handleMouseLeave = () => {
    logoutTimeoutRef.current = setTimeout(() => {
      setShowLogout(false);
    }, 200); // Allows user time to move mouse to Logout button
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    window.location.href = '/'; // or use navigate('/')
  };

  const statusOptions = [
  'All',
  'Qualification',
  'Development',
  'Submitted',
  'Hold',
  'Engaged',
  'Dead',
  'Forecasted'  // <-- Add this if you have it in your data
  ];
const filteredOpportunityData = opportunityData.filter(opportunity => {
  const statusMatch =
    statusFilter === 'All' ||
    (
      opportunity.status === statusFilter &&
      (opportunity.progress_status === 'Not Started' || opportunity.progress_status === 'In Progress')
    );

  const priorityMatch =
    priorityFilter === 'All' || opportunity.priority === priorityFilter;

  const nameMatch =
    sheetSearch.trim() === '' ||
    (opportunity.project_name || '')
      .toLowerCase()
      .includes(sheetSearch.trim().toLowerCase());

  return statusMatch && priorityMatch && nameMatch;
});





   // Group opportunities into chunks of 5 for row layout
function chunkArray<T>(arr: T[], size: number): T[][] {
  return arr.length > 0 ? [arr.slice(0, size), ...chunkArray(arr.slice(size), size)] : [];
}

const handleStatusChange = async (opportunity_id: string, newStatus: StatusType, regionCode: string) => {
  try {
    // Example API call—replace URL & payload as needed
    const url = `https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=update_opportunity_progress_status`;
    await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        opportunity_id,
        progress_status: newStatus,
      }),
      headers: { 'Content-Type': 'application/json' },
    });
    // Optionally: refresh data here
    fetchOpportunities(regionCode);
  } catch (error) {
    alert('Failed to update status.');
  }
};


const renderSheetView = () => {
  const cardsPerRow = 5;
  const rows = chunkArray(filteredOpportunityData, cardsPerRow);

  {loading && (
  <div className="loading-overlay">
    <div className="spinner"></div>
    <div style={{ marginTop: 16 }}>Loading opportunities...</div>
  </div>
)}

  return (
    <div className="opportunity-sheet-view">
      {rows.map((row: Opportunity[], i: number) => (
        <div className="sheet-row" key={i} style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          {row.map((op: Opportunity) => {
          const priorityObj = priorityIcons[op.priority] || priorityIcons['Low'];
          const { icon, color, label } = priorityObj;

          return (
            <div
              className="sheet-card"
              key={op.opportunity_id}
              style={{
                flex: 1,
                minWidth: 200,
                minHeight: 130,
                border: '1.5px solid #d0d4da',
                borderRadius: 16,
                background: 'linear-gradient(140deg, #f8fafc 60%, #e3effa 100%)',
                boxShadow: '0 2px 12px #0001',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: '22px 18px',
                transition: 'box-shadow 0.15s, border-color 0.15s',
                cursor: 'pointer',
                marginRight: 8,
              }}
              onClick={() => setSelectedCardData(op)}
              // ...onClick here if needed...
            >

<div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
  {STATUS_OPTIONS.map((status) => {
  const s = statusIcons[status as StatusType];
  const isSelected = op.progress_status === status;

  // If this status is selected, use "In Progress" style (not its own style)
  const selectedStyle = statusIcons['In Progress'];

  return (
    <span
      key={status}
      title={isSelected ? `Current: ${s.label}` : `Mark as ${s.label}`}
      style={{
        color: isSelected ? selectedStyle.color : s.color,
        fontSize: 22,
        cursor: isSelected ? 'default' : 'pointer',
        opacity: isSelected ? 1 : 0.6,
        border: isSelected
          ? `2.5px solid ${selectedStyle.color}`
          : '1.5px solid #e0e0e0',
        borderRadius: '50%',
        padding: 3,
        background: isSelected ? '#f0fff0' : '#fff',
        marginRight: 6,
        boxShadow: isSelected
          ? `0 2px 8px ${selectedStyle.color}33`
          : undefined,
        transition: 'all 0.18s',
      }}
      onClick={e => {
        e.stopPropagation();
        if (!isSelected) {
          handleStatusChange(op.opportunity_id, status as StatusType, regionCode || '');
        }
      }}
    >
      {s.icon}
    </span>
  );
})}

 </div>



              <div style={{ fontWeight: 600, marginBottom: 6 }}>{op.project_name}</div>
              <div style={{ marginBottom: 4 }}>
                <span
                  style={{
                    fontSize: 20,
                    color,
                    marginRight: 8,
                    verticalAlign: 'middle',
                    display: 'inline-block',
                  }}
                  title={label}
                >
                  {icon}
                </span>
                <span style={{ fontSize: 13, color: '#666' }}>
                  {label}
                </span>
              </div>
              {(op.start_date || op.due_date) && (
                <div style={{ fontSize: 13, color: '#666' }}>
                  {op.start_date && <>Start: {op.start_date}<br /></>}
                  {op.due_date && <>Due: {op.due_date}</>}
                </div>
              )}
            </div>
          );
        })}

          {/* Fill empty spots to keep layout */}
          {row.length < cardsPerRow &&
            Array(cardsPerRow - row.length)
              .fill(null)
              .map((_, idx) => (
                <div key={'empty-' + idx} style={{ flex: 1, minWidth: 200 }} />
              ))}
        </div>
      ))}
    </div>
  );
};

  return (
    <div className="landing-container">
      <header className="landing-header">
        <img src={logo} alt="Design Group Logo" className="landing-logo" />
        <h1 className="landing-title">Staffing Portal - Opportunity</h1>

        {/* User Icon on the right */}
        <div className="user-icon-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}      
        >
          <span className="username-text">{username}</span>
          <img src={userIcon} alt="User Icon" className="user-icon" />
          {showLogout && (
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
          )}
        </div>
      </header>

      {/* Sidebar Navigation */}
      <Navigation
        regions={regions}
        expandedRegion={expandedRegion}
        selectedSubLink={selectedSubLink}
        toggleRegion={toggleRegion}
        setSelectedSubLink={setSelectedSubLink}
      />

      <main className="landing-main">
        {/* Search Bar for Grid */}
        <h2>Opportunities for Region: {regionCode}</h2>
<div className="search-filter-row">
  <div className="status-filter-section">
    {/* existing Priority Filter */}
    <label htmlFor="priority-filter" style={{ marginRight: '8px' }}>Priority:</label>
    <select
      id="priority-filter"
      value={priorityFilter}
      onChange={(e) => setPriorityFilter(e.target.value)}
      style={{ padding: '6px 12px', borderRadius: '4px', marginRight: 12 }}
    >
      {priorityOptions.map(option => (
        <option key={option} value={option}>{option}</option>
      ))}
    </select>

    {/* NEW: Project name filter for sheet view */}
    <input
      type="text"
      placeholder="Search project name…"
      value={sheetSearch}
      onChange={(e) => setSheetSearch(e.target.value)}
      style={{ padding: '6px 12px', borderRadius: '4px', minWidth: 240 }}
    />
  </div>
</div>

        <div className="dark-teal-addopportunity-container">
          <button className="dark-teal-addopportunity-button" onClick={() => setIsAddModalOpen(true)}>
             + Add Opportunity
          </button>
        </div>

        {/* Opportunity Data Grid */}
         {displaySheetView ? renderSheetView() : (
        <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
          <AgGridReact
            rowData={filteredOpportunityData}
            columnDefs={columnDefs}
            defaultColDef={{
              sortable: true,
              filter: true,
              flex: 1,
            }}
            onRowClicked={(row) => onRowClicked(row.data)}
            onGridReady={onGridReady}
            quickFilterText={quickFilterText}
            pagination={true} // Enable pagination
            paginationPageSize={100} // Rows per page
          />
        </div>
         )}

        {/* Opportunity Sliding Drawer */}
        {selectedRowData && (
          <div ref={drawerRef}>
            <OpportunitySlidingDrawer
              isOpen={isDrawerOpen}
              selectedRowData={selectedRowData}
              onClose={() => setIsDrawerOpen(false)}
              onUpdateSuccess={() => fetchOpportunities(regionCode || '')}
            />
          </div>
        )}

        <AddOpportunityModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          regionCode={regionCode || ''}
          onSaveSuccess={() => {
            fetchOpportunities(regionCode || '');
            setIsAddModalOpen(false);
          }}
        />

        {/* Modal for selected card */}
        {selectedCardData && (
          <OpportunitySheetModal
            opportunity={selectedCardData}
            onClose={() => {
              // Refresh opportunity data when modal closes!
              fetchOpportunities(regionCode || '');
              setSelectedCardData(null);
            }}
            onSaveSuccess={() => {
              fetchOpportunities(regionCode || '');
              setSelectedCardData(null);
            }}
            // pass handlers to update notes/labels/mannings as needed
          />
        )}

      </main>
    </div>
  );
};

export default Opportunity;
